<template>
  <!-- POC Cuidados Paliativos (Tela 9): rede de matriciamento da equipe matricial.
       Quem pede apoio à equipe, com que volume e tempo de resposta, e sobre o quê.
       Só as equipes assistenciais da própria macro (role 2 não vê outras matriciais). -->
  <div class="msn">
    <!-- Resumo -->
    <div class="msn-tiles">
      <div class="msn-card msn-tile">
        <p class="msn-tile-value">
          {{ totalRequests }}
        </p>
        <p class="msn-tile-label">
          pedidos de apoio recebidos no mês
        </p>
        <p class="msn-tile-note">
          de {{ rows.filter((r) => r.total > 0).length }} equipes assistenciais
        </p>
      </div>
      <div class="msn-card msn-tile">
        <p class="msn-tile-value">
          {{ fmtDays(averageResponse) }} <span class="msn-tile-of">dia</span>
        </p>
        <p class="msn-tile-label">
          de resposta média
        </p>
      </div>
      <div class="msn-card msn-tile">
        <p class="msn-tile-value">
          {{ topReason.pct }}%
        </p>
        <p class="msn-tile-label">
          dos pedidos são sobre {{ topReason.name.toLowerCase() }}
        </p>
        <p class="msn-tile-note">
          motivo mais frequente na macro
        </p>
      </div>
    </div>

    <!-- Onde capacitar -->
    <section
      v-if="insights.length > 0"
      class="msn-section"
    >
      <h2 class="msn-section-title">
        Onde capacitar
        <span class="msn-section-sub">equipes com pedidos concentrados num mesmo motivo</span>
      </h2>
      <div class="msn-insights">
        <article
          v-for="insight in insights"
          :key="insight.unitId"
          class="msn-card msn-insight"
        >
          <p class="msn-insight-title">
            {{ insight.teamName }}: <strong>{{ insight.pct }}%</strong> dos pedidos são sobre
            {{ insight.reason.toLowerCase() }}
          </p>
          <p class="msn-insight-text">
            {{ insight.count }} de {{ insight.total }} pedidos no mês. Uma ação educativa com a equipe
            (procedimento 03.11.02.002-0) pode reduzir a dependência de apoio nesse tema.
          </p>
        </article>
      </div>
    </section>

    <!-- Quem pede apoio -->
    <section class="msn-section">
      <h2 class="msn-section-title">
        Quem pede apoio à equipe
        <span class="msn-section-sub">{{ referenceMonth }}</span>
      </h2>

      <div class="msn-card msn-table-card">
        <table class="msn-table">
          <thead>
            <tr>
              <th>Equipe assistencial</th>
              <th class="msn-col-bar">
                Pedidos no mês
              </th>
              <th class="is-number">
                Resposta média
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.unitId"
            >
              <td>
                <router-link
                  v-if="row.teamId"
                  :to="{ name: 'palliativeTeamDetail', params: { id: row.teamId }, query: { de: 'matriciamento' } }"
                  class="msn-team-link"
                >
                  {{ row.teamName }}
                </router-link>
                <span
                  v-else
                  class="msn-team-name"
                >
                  {{ row.teamName }}
                </span>
                <span class="msn-muted">{{ row.municipality }}</span>
              </td>
              <td class="msn-col-bar">
                <span class="msn-volume">
                  <span
                    class="msn-volume-track"
                    aria-hidden="true"
                  >
                    <span
                      class="msn-volume-fill"
                      :style="{ width: `${(row.total / maxTotal) * 100}%` }"
                    />
                  </span>
                  <span class="msn-volume-value">{{ row.total }}</span>
                </span>
              </td>
              <td class="is-number">
                {{ row.total > 0 && row.averageResponseDays != null ? `${fmtDays(row.averageResponseDays)} dia` : "—" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Motivos por equipe: mapa de calor -->
    <section class="msn-section">
      <h2 class="msn-section-title">
        Motivos dos pedidos por equipe
        <span class="msn-section-sub">quanto mais escuro, maior a parte dos pedidos da equipe</span>
      </h2>

      <div class="msn-card msn-table-card">
        <table class="msn-table msn-heatmap">
          <thead>
            <tr>
              <th>Equipe assistencial</th>
              <th
                v-for="reason in reasons"
                :key="reason._id"
                class="is-center"
              >
                {{ reason.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.unitId"
            >
              <td class="msn-strong">
                {{ row.teamName }}
              </td>
              <td
                v-for="reason in reasons"
                :key="reason._id"
                class="is-center"
              >
                <span
                  class="msn-cell"
                  :style="cellStyle(row, reason._id)"
                  :aria-label="`${row.teamName}, ${reason.name}: ${row.reasons[reason._id]} pedidos`"
                >{{ row.reasons[reason._id] || "" }}</span>
              </td>
            </tr>
            <tr class="msn-total-row">
              <td class="msn-strong">
                Total da macro
              </td>
              <td
                v-for="reason in reasons"
                :key="reason._id"
                class="is-center msn-strong"
              >
                {{ reasonTotals[reason._id] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import { REASON_OPTIONS } from "@/services/palliative/matrixSupportApi";
import { getMatrixSupportNetwork } from "@/services/palliative/networkApi";
import { pocLiveSync } from "@/services/palliative/pocLiveSync";

export default {
  name: "MatrixSupportNetwork",
  // Pedidos novos entram sem recarregar a página.
  mixins: [pocLiveSync("reload")],
  data() {
    return {
      reasons: REASON_OPTIONS,
      referenceMonth: "",
      teams: [],
      apiInsights: []
    };
  },
  computed: {
    rows() {
      return [...this.teams].sort((a, b) => b.total - a.total);
    },
    totalRequests() {
      return this.rows.reduce((sum, r) => sum + r.total, 0);
    },
    maxTotal() {
      return Math.max(1, ...this.rows.map((r) => r.total));
    },
    // Média ponderada pelo volume de pedidos respondidos de cada equipe.
    averageResponse() {
      const withAverage = this.rows.filter((r) => r.total > 0 && r.averageResponseDays != null);
      const weight = withAverage.reduce((sum, r) => sum + r.total, 0);
      if (!weight) return null;
      return withAverage.reduce((sum, r) => sum + r.averageResponseDays * r.total, 0) / weight;
    },
    reasonTotals() {
      const totals = {};
      this.reasons.forEach((reason) => {
        totals[reason._id] = this.rows.reduce((sum, r) => sum + (r.reasons[reason._id] || 0), 0);
      });
      return totals;
    },
    topReason() {
      const top = [...this.reasons].sort(
        (a, b) => this.reasonTotals[b._id] - this.reasonTotals[a._id]
      )[0];
      const pct = this.totalRequests ? Math.round((this.reasonTotals[top._id] / this.totalRequests) * 100) : 0;
      return { name: top.name, pct };
    },
    // "Onde capacitar" vem da API (4+ pedidos e metade ou mais num só motivo). "Outro" não sugere capacitação.
    insights() {
      return this.apiInsights
        .filter((insight) => insight.reason !== "outro")
        .map((insight) => {
          const reason = this.reasons.find((r) => r._id === insight.reason);
          return {
            ...insight,
            teamId: insight.unitId,
            reason: reason ? reason.name : insight.reason
          };
        });
    }
  },
  created() {
    this.reload();
  },
  methods: {
    async reload() {
      try {
        const data = await getMatrixSupportNetwork();
        this.referenceMonth = data.referenceMonth;
        this.teams = data.teams;
        this.apiInsights = data.insights;
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar a rede de matriciamento.", error);
      }
    },
    fmtDays(value) {
      if (value == null) return "—";
      return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },
    // Intensidade pela parte dos pedidos da equipe naquele motivo (um tom só).
    cellStyle(row, reasonId) {
      const count = row.reasons[reasonId] || 0;
      if (!count || !row.total) return {};
      const share = count / row.total;
      const alpha = 0.12 + share * 0.88;
      return {
        backgroundColor: `rgba(79, 63, 130, ${alpha.toFixed(2)})`,
        color: share >= 0.45 ? "#ffffff" : "#2b2b3a"
      };
    }
  }
};
</script>

<style lang="scss" scoped>
$ink: #2b2b3a;
$ink-muted: #6b6b7b;
$primary: #4f3f82;
$primary-soft: #ece9f5;
$grid: #e4e2ea;

.msn {
  color: $ink;
}

.msn-card {
  padding: 20px 25px;
  border-radius: 15px;
  background-color: #ffffff;
  box-shadow: 2px 4px 7px 0 rgba(0, 0, 0, 0.1);
}

.msn-section {
  margin-top: 32px;
}

.msn-section-title {
  margin-bottom: 12px;
  font-size: 18px;
  font-weight: 700;
}

.msn-section-sub {
  margin-left: 6px;
  font-size: 13px;
  font-weight: 400;
  color: $ink-muted;
}

/* Resumo */
.msn-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.msn-tile-value {
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
}

.msn-tile-of {
  font-size: 16px;
  font-weight: 400;
  color: $ink-muted;
}

.msn-tile-label {
  font-size: 14px;
  line-height: 20px;
}

.msn-tile-note {
  margin-top: 6px !important;
  font-size: 12px;
  color: $ink-muted;
}

/* Onde capacitar */
.msn-insights {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.msn-insight {
  border-left: 4px solid $primary;
}

.msn-insight-title {
  font-size: 15px;
  line-height: 21px;

  strong {
    color: $primary;
  }
}

.msn-insight-text {
  margin-top: 6px !important;
  font-size: 13px;
  line-height: 18px;
  color: $ink-muted;
}

/* Tabelas */
.msn-table-card {
  overflow-x: auto;
}

.msn-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th {
    padding: 0 12px 10px 0;
    border-bottom: 1px solid $grid;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    color: $ink-muted;
  }

  td {
    padding: 10px 12px 10px 0;
    border-bottom: 1px solid $grid;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .is-number {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .is-center {
    text-align: center;
  }
}

.msn-team-name {
  display: block;
  font-weight: 600;
}

.msn-team-link {
  display: block;
  font-weight: 600;
  color: $primary;

  &:hover {
    text-decoration: underline;
  }
}

.msn-muted {
  display: block;
  font-size: 12px;
  color: $ink-muted;
}

.msn-strong {
  font-weight: 600;
}

.msn-col-bar {
  width: 45%;
}

.msn-volume {
  display: flex;
  align-items: center;
  gap: 10px;
}

.msn-volume-track {
  flex: 1;
  height: 12px;
  border-radius: 6px;
  background-color: $primary-soft;
  overflow: hidden;
}

.msn-volume-fill {
  display: block;
  height: 100%;
  border-radius: 6px;
  background-color: $primary;
}

.msn-volume-value {
  width: 28px;
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.msn-live {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: $primary;
}

/* Mapa de calor */
.msn-heatmap {
  th.is-center {
    min-width: 96px;
    line-height: 16px;
  }
}

.msn-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 32px;
  border-radius: 6px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.msn-total-row td {
  border-top: 2px solid $grid;
}

@media (max-width: 1000px) {
  .msn-tiles,
  .msn-insights {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
